import { warnLog } from './logger';

/**
 * Timezone-aware time utilities for international users
 */

/**
 * Get user's timezone
 * @returns {string} IANA timezone identifier (e.g., 'America/New_York', 'Asia/Shanghai')
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    warnLog('Failed to detect user timezone, falling back to UTC');
    return 'UTC';
  }
};

/**
 * Convert UTC timestamp to user's local time
 * @param {string|Date} utcTimestamp - UTC timestamp (ISO string or Date object)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local time string
 */
export const formatLocalTime = (utcTimestamp, options = {}) => {
  if (!utcTimestamp) return '';
  
  try {
    const date = new Date(utcTimestamp);
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    
    return date.toLocaleString(undefined, { ...defaultOptions, ...options });
  } catch (error) {
    warnLog('Failed to format time:', error);
    return String(utcTimestamp);
  }
};

/**
 * Format time for display with timezone info
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Formatted time with timezone
 */
export const formatTimeWithTimezone = (utcTimestamp) => {
  return formatLocalTime(utcTimestamp, {
    timeZoneName: 'short'
  });
};

/**
 * Get relative time (e.g., "2 minutes ago", "in 5 hours")
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string
 */
export const getRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return '';
  
  try {
    const date = new Date(utcTimestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return formatLocalTime(utcTimestamp, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    warnLog('Failed to calculate relative time:', error);
    return String(utcTimestamp);
  }
};

/**
 * Format session expiry time with warning
 * @param {string|Date} expiryTimestamp - UTC expiry timestamp
 * @returns {Object} Formatted expiry info with warning level
 */
export const formatSessionExpiry = (expiryTimestamp) => {
  if (!expiryTimestamp) {
    return { text: 'Unknown', level: 'warning' };
  }
  
  try {
    const expiryDate = new Date(expiryTimestamp);
    const now = new Date();
    const diffMs = expiryDate - now;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMs <= 0) {
      return { text: 'Expired', level: 'error' };
    } else if (diffMinutes < 5) {
      return { 
        text: `Expires in ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`, 
        level: 'error' 
      };
    } else if (diffMinutes < 15) {
      return { 
        text: `Expires in ${diffMinutes} minutes`, 
        level: 'warning' 
      };
    } else {
      const localTime = formatLocalTime(expiryTimestamp, {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
      return { 
        text: `Expires at ${localTime}`, 
        level: 'info' 
      };
    }
  } catch (error) {
    warnLog('Failed to format session expiry:', error);
    return { text: 'Unknown', level: 'warning' };
  }
};

/**
 * Check if timestamp is in the future (accounting for timezone)
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {boolean} True if timestamp is in the future
 */
export const isFutureTime = (utcTimestamp) => {
  if (!utcTimestamp) return false;
  
  try {
    const date = new Date(utcTimestamp);
    const now = new Date();
    return date > now;
  } catch (error) {
    return false;
  }
};

/**
 * Get current UTC timestamp as ISO string
 * @returns {string} Current UTC timestamp
 */
export const getCurrentUTCTimestamp = () => {
  return new Date().toISOString();
};
